import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash, Plus } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL || '';
const COND_LABEL = { '': 'Sempre', idade_ate_12m: 'Bebé até 12 meses', idade_12m_mais: 'Bebé com 12 meses ou mais', nunca_login: 'Paciente nunca fez login', avulsa: 'Consulta avulsa', em_programa: 'Dentro de programa' };
const selCls = 'border border-brand-border rounded-lg px-2 h-9 text-xs bg-white';

function Sel({ value, onChange, options, tid }) {
  return <select value={value} onChange={e => onChange(e.target.value)} className={selCls} data-testid={tid}>{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
}

function ItemRow({ it, set, up, down, remove, meta, tid, isRemoveOp, keyOptions }) {
  const groups = meta.groups.map(g => [g, g === 'antes' ? 'Antes da consulta' : g]);
  const conds = meta.conditions.map(c => [c, COND_LABEL[c] || c]);
  const autos = [['', 'Manual'], ...Object.entries(meta.auto_sources).map(([k, v]) => [k, `Auto: ${v.slice(0, 38)}…`])];
  return (
    <div className="flex flex-wrap items-center gap-1.5 py-1.5 border-t border-brand-border/40 first:border-t-0" data-testid={tid}>
      {isRemoveOp ? (
        <>
          <span className="text-xs text-brand-muted w-16">Remover</span>
          <Sel value={it.key || ''} onChange={v => set({ key: v })} options={[['', 'Escolher tarefa…'], ...keyOptions]} tid={`${tid}-key`} />
        </>
      ) : (
        <>
          <Input value={it.label} onChange={e => set({ label: e.target.value })} className="h-9 text-xs flex-1 min-w-[180px]" placeholder="Texto da tarefa" data-testid={`${tid}-label`} />
          <Sel value={it.group} onChange={v => set({ group: v })} options={groups} tid={`${tid}-group`} />
          <Sel value={it.auto_source || ''} onChange={v => set({ auto_source: v })} options={autos} tid={`${tid}-auto`} />
        </>
      )}
      <Sel value={it.condition || ''} onChange={v => set({ condition: v })} options={conds} tid={`${tid}-cond`} />
      <button onClick={up} className="p-1.5 text-brand-muted hover:text-brand-text" aria-label="Subir" data-testid={`${tid}-up`}><ArrowUp size={14} /></button>
      <button onClick={down} className="p-1.5 text-brand-muted hover:text-brand-text" aria-label="Descer" data-testid={`${tid}-down`}><ArrowDown size={14} /></button>
      <button onClick={remove} className="p-1.5 text-brand-placeholder hover:text-red-500" aria-label="Remover" data-testid={`${tid}-remove`}><Trash size={14} /></button>
    </div>
  );
}

const move = (arr, i, d) => { const j = i + d; if (j < 0 || j >= arr.length) return arr; const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; return c; };

export default function TaskTemplatesEditor() {
  const confirmDelete = useConfirmDelete();
  const [meta, setMeta] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [programas, setProgramas] = useState({});
  const [activeFrom, setActiveFrom] = useState('2026-10-01');
  const [prog, setProg] = useState('introducao_alimentar');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/consult-tasks/templates`).then(({ data }) => { setMeta(data); setTipos(data.tipos); setProgramas(data.programas || {}); setActiveFrom(data.active_from); })
      .catch(() => toast.error('Erro ao carregar modelos'));
  }, []);
  if (!meta) return null;

  const setItems = (ti, fn) => setTipos(ts => ts.map((t, i) => i === ti ? { ...t, items: fn(t.items) } : t));
  const ops = (tk) => (programas[prog] || {})[tk] || [];
  const setOps = (tk, fn) => setProgramas(p => ({ ...p, [prog]: { ...(p[prog] || {}), [tk]: fn(((p[prog] || {})[tk]) || []) } }));
  const confirmRemove = async (what) => confirmDelete({ description: `Vai remover ${what} do modelo. Só afeta consultas futuras ou ainda por fazer.`, confirmLabel: 'Remover' });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/consult-tasks/templates`, { tipos, programas, active_from: activeFrom });
      setTipos(data.tipos); setProgramas(data.programas || {}); toast.success('Modelos guardados');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar modelos'); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-10" data-testid="task-templates-editor">
      <h2 className="font-heading text-xl text-brand-text mb-1">Modelos de tarefas por consulta</h2>
      <p className="text-sm text-brand-muted mb-4">Alterar um modelo só afeta consultas futuras (ou tarefas ainda por fazer). As consultas passadas mantêm a cópia que tinham.</p>

      {tipos.map((t, ti) => (
        <div key={t.key} className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid={`tpl-tipo-${t.key}`}>
          <div className="flex flex-wrap gap-2 mb-2">
            <Input value={t.label} onChange={e => setTipos(ts => ts.map((x, i) => i === ti ? { ...x, label: e.target.value } : x))} className="h-9 text-sm font-medium max-w-[240px]" data-testid={`tpl-tipo-label-${t.key}`} />
            <Input value={t.short} onChange={e => setTipos(ts => ts.map((x, i) => i === ti ? { ...x, short: e.target.value } : x))} className="h-9 text-xs max-w-[160px]" placeholder="Abreviatura" data-testid={`tpl-tipo-short-${t.key}`} />
            {!['primeira', 'seguimento'].includes(t.key) && (
              <button onClick={async () => { if (await confirmRemove(`o tipo "${t.label}"`)) setTipos(ts => ts.filter((_, i) => i !== ti)); }} className="text-xs text-red-500 ml-auto" data-testid={`tpl-tipo-remove-${t.key}`}>Remover tipo</button>
            )}
          </div>
          {t.items.map((it, ii) => (
            <ItemRow key={it.key + ii} it={it} meta={meta} tid={`tpl-${t.key}-item-${ii}`}
              set={patch => setItems(ti, items => items.map((x, i) => i === ii ? { ...x, ...patch } : x))}
              up={() => setItems(ti, items => move(items, ii, -1))} down={() => setItems(ti, items => move(items, ii, 1))}
              remove={async () => { if (await confirmRemove(`a tarefa "${it.label}"`)) setItems(ti, items => items.filter((_, i) => i !== ii)); }} />
          ))}
          <button onClick={() => setItems(ti, items => [...items, { key: '', label: '', group: 'Extra', condition: '', auto_source: '' }])} className="mt-2 text-xs text-[#657161] flex items-center gap-1" data-testid={`tpl-${t.key}-add-item`}><Plus size={12} /> Adicionar tarefa</button>
        </div>
      ))}
      <button onClick={() => setTipos(ts => [...ts, { key: '', label: 'Novo tipo de consulta', short: 'Novo tipo', items: [] }])} className="text-xs text-[#657161] flex items-center gap-1 mb-6" data-testid="tpl-add-tipo"><Plus size={12} /> Adicionar tipo de consulta</button>

      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="tpl-programs">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <h3 className="font-heading text-sm text-brand-text">Acréscimos e substituições por programa</h3>
          <Sel value={prog} onChange={setProg} options={meta.programs.map(p => [p.key, p.label])} tid="tpl-program-select" />
        </div>
        <p className="text-[11px] text-brand-muted mb-2">Ex.: Super Bebés — até 12 meses substitui “Equivalentes definidos” pela ementa; com 12 meses ou mais mantém os equivalentes e acrescenta a ementa.</p>
        {tipos.map(t => (
          <div key={t.key} className="mb-3" data-testid={`tpl-prog-tipo-${t.key}`}>
            <p className="text-xs font-semibold text-[#657161]">{t.label}</p>
            {ops(t.key).map((op, oi) => (
              <ItemRow key={oi} it={op} meta={meta} isRemoveOp={op.op === 'remove'} keyOptions={t.items.filter(i => i.key).map(i => [i.key, i.label])} tid={`tpl-prog-${t.key}-op-${oi}`}
                set={patch => setOps(t.key, l => l.map((x, i) => i === oi ? { ...x, ...patch } : x))}
                up={() => setOps(t.key, l => move(l, oi, -1))} down={() => setOps(t.key, l => move(l, oi, 1))}
                remove={async () => { if (await confirmRemove('esta regra')) setOps(t.key, l => l.filter((_, i) => i !== oi)); }} />
            ))}
            <div className="flex gap-3 mt-1">
              <button onClick={() => setOps(t.key, l => [...l, { op: 'add', key: '', label: '', group: 'Entrega clínica', condition: '', auto_source: '' }])} className="text-xs text-[#657161]" data-testid={`tpl-prog-${t.key}-add`}>+ Acrescentar tarefa</button>
              <button onClick={() => setOps(t.key, l => [...l, { op: 'remove', key: '', condition: '' }])} className="text-xs text-[#657161]" data-testid={`tpl-prog-${t.key}-remove-op`}>+ Retirar tarefa</button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4 text-xs text-brand-muted">
        <span>Tarefas ativas a partir de</span>
        <Input type="date" value={activeFrom} onChange={e => setActiveFrom(e.target.value)} className="h-9 w-40 text-xs" data-testid="tpl-active-from" />
      </div>
      <Button onClick={save} disabled={saving} className="bg-brand-green text-white" data-testid="tpl-save-btn">{saving ? 'A guardar…' : 'Guardar modelos'}</Button>
    </div>
  );
}
